import { CHAT_HISTORICO, CHAT_MAX_CHARS } from '@sol-pool/protocol'
import { t } from '@/i18n'

/**
 * A conversa da mesa, do lado de quem a lê.
 *
 * Lógica pura — sem DOM — para poder ser testada: a fila que guarda as últimas
 * mensagens, o rótulo de quem falou e a conferência do que se pode mandar. O
 * `netmain.ts` só desenha o que sai daqui.
 */

export type MensagemDeChat = {
  /** Índice do jogador, ou `null` para espectador. */
  by: 0 | 1 | null
  /** Endereço abreviado de quem falou. */
  who: string
  text: string
  /** Hora do servidor, em ms. */
  at: number
}

/**
 * Acrescenta uma mensagem e descarta as mais velhas além do histórico.
 *
 * Imutável: devolve uma fila nova. O estado da partida é substituído a cada
 * `patch`, e uma fila mutada por baixo faria dois estados compartilharem o
 * mesmo array — o "antes" mudaria junto com o "depois".
 */
export function enfileirar(
  fila: readonly MensagemDeChat[],
  nova: MensagemDeChat,
  maximo = CHAT_HISTORICO,
): MensagemDeChat[] {
  const junta = [...fila, nova]
  return junta.length > maximo ? junta.slice(junta.length - maximo) : junta
}

/**
 * Quem falou, do ponto de vista de quem lê.
 *
 * O jogador vê "você" na própria fala; todo o resto — o adversário, um
 * espectador, os dois jogadores para quem assiste — aparece por `who`, que é
 * o apelido de quem falou ou, sem apelido, o endereço abreviado. Antes o
 * adversário era só "adversário"; com apelidos, ele tem nome.
 */
export function rotuloDoAutor(m: MensagemDeChat, eu: 0 | 1 | null): string {
  if (eu !== null && m.by === eu) return t('chat.voce')
  return m.who
}

/**
 * A fila que o servidor mandou em `chat.history`, no lugar da atual.
 *
 * Substitui, não junta: o histórico é o que a mesa tem, e juntar repetiria o
 * que este cliente já tinha recebido pela sala antes de trocar de página.
 */
export function substituirHistorico(
  mensagens: readonly MensagemDeChat[],
  maximo = CHAT_HISTORICO,
): MensagemDeChat[] {
  return mensagens.length > maximo ? mensagens.slice(mensagens.length - maximo) : [...mensagens]
}

/**
 * O texto pode ir?
 *
 * A mesma regra do servidor, aplicada antes de mandar — o cliente não é a
 * defesa, é a cortesia: recusar aqui poupa uma viagem e um erro na tela.
 */
export function podeEnviar(texto: string): boolean {
  const limpo = texto.trim()
  return limpo.length > 0 && [...limpo].length <= CHAT_MAX_CHARS
}

/** Mensagens seguidas da mesma pessoa, com até este intervalo, viram um grupo. */
export const JANELA_DO_GRUPO_MS = 5 * 60_000

export type GrupoDeChat = {
  /** De quem é, do ponto de vista de quem lê: decide o lado e a cor do balão. */
  tom: 'eu' | 'outro' | 'plateia'
  /** O rótulo de quem falou (`rotuloDoAutor`), escrito uma vez por grupo. */
  autor: string
  mensagens: MensagemDeChat[]
}

/**
 * As mensagens em GRUPOS: a mesma pessoa falando em sequência escreve o nome
 * uma vez só, com os balões empilhados embaixo.
 *
 * Antes cada linha repetia o nome — "Draven eae", "Draven tem alguém aí?" —,
 * e metade da coluna estreita era o mesmo apelido. Mesma pessoa é o mesmo
 * `by` E o mesmo `who`: na plateia `by` é nulo para todo espectador, e só o
 * `who` separa um do outro. Uma pausa longa abre grupo novo, para a hora do
 * cabeçalho não mentir sobre a conversa que veio depois.
 */
export function agruparMensagens(mensagens: readonly MensagemDeChat[], eu: 0 | 1 | null): GrupoDeChat[] {
  const grupos: GrupoDeChat[] = []
  for (const m of mensagens) {
    const ultimo = grupos.at(-1)
    const anterior = ultimo?.mensagens.at(-1)
    if (ultimo && anterior && anterior.by === m.by && anterior.who === m.who && m.at - anterior.at <= JANELA_DO_GRUPO_MS) {
      ultimo.mensagens.push(m)
      continue
    }
    const tom = m.by === null ? 'plateia' : m.by === eu ? 'eu' : 'outro'
    grupos.push({ tom, autor: rotuloDoAutor(m, eu), mensagens: [m] })
  }
  return grupos
}
